import classNames from "classnames";

import { FC, ReactElement, useCallback, useRef, useState } from "react";
import useI18n from "../../hooks/useTranslations";
import styles from "./DragAndDrop.module.css";
import LoadingSpinner from "./LoadingSpinner";

interface DragAndDropProps {
  element: ReactElement;
  onDrop: (files: FileList, onDone: () => void) => void;
}

const DragAndDrop: FC<DragAndDropProps> = ({ element, onDrop }) => {
  const i18n = useI18n();
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dragDepth = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragging(false);

    if (e.dataTransfer.files.length > 0) {
      setIsSubmitting(true);
      onDrop(e.dataTransfer.files, () => {
        setIsSubmitting(false);
      });
    }
  }, [onDrop]);

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={styles.dragAndDropContainer}
    >
      <div className={styles.content}>
        <div
          className={classNames(styles.element, {
            [styles.elementHidden]: isDragging || isSubmitting,
          })}
        >
          {element}
        </div>
        {(isDragging || isSubmitting) && (
          <div className={classNames(styles.overlay, styles.drop)}>
            {isDragging && !isSubmitting && <div>{i18n.translation.common.dropIt}</div>}
            {isSubmitting && (
              <div>
                <LoadingSpinner />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DragAndDrop;
